(function () {
  var config = window.__currencyEstimateRates;
  if (!config || !config.enabled) return;

  var els = document.querySelectorAll('[data-currency-estimate]');
  if (!els.length) return;

  var codes = (config.codes || '')
    .split(',')
    .map(function (c) { return c.trim().toUpperCase(); })
    .filter(Boolean);
  if (!codes.length) return;

  // Manual overrides win over the live source. A currency only counts as
  // "overridden" if its setting is an actual number, blank/null falls
  // through to the live rate.
  var overrides = config.overrides || {};
  // A rate override counts only when it's a positive number. The fields now
  // default to 0 (they previously had no default at all, which left 20 numeric
  // inputs in the theme editor bound to nothing), so 0 has to mean "no
  // override, use the live rate" rather than "convert at a rate of zero".
  function isOverridden(c) {
    return typeof overrides[c] === 'number' && overrides[c] > 0;
  }
  var manualCodes = codes.filter(isOverridden);
  var autoCodes = codes.filter(function (c) { return !isOverridden(c); });

  var CURRENCY_CACHE_KEY = 'ss_geo_currency_v2';
  var RATES_CACHE_KEY = 'ss_fx_rates_idr_auto_v3'; // v3: new source, and a real unit-conversion fix, not just a cache-shape change

  function render(currencyCode, rateTable) {
    var rate = rateTable && rateTable[currencyCode];
    if (!rate) return false; // not on the list, or rate missing — show nothing, not a broken estimate
    var didRender = false;
    els.forEach(function (el) {
      var baseCents = parseInt(el.getAttribute('data-base-amount'), 10);
      if (isNaN(baseCents)) return;
      var basePriceIdr = baseCents / 100;
      var converted = basePriceIdr / rate; // rate is IDR per 1 unit of currencyCode

      // Sanity guard. IDR is one of the world's lowest-value units, so a real
      // conversion out of IDR always produces a smaller number (the weakest
      // case, VND, is only about 1.6x larger). Anything wildly bigger means the
      // rate was read in the wrong direction, not that the price is genuinely
      // enormous. Show nothing rather than a figure like "CHF 40,000,000,000",
      // which is exactly what a unit inversion put on a live product page.
      if (!isFinite(converted) || converted > basePriceIdr * 100) return;
      try {
        var formatted = new Intl.NumberFormat(undefined, { style: 'currency', currency: currencyCode }).format(converted);
        el.textContent = '\u2248 ' + formatted;
        didRender = true;
      } catch (e) {
        // Unrecognized currency code — fail silently, no broken text shown.
      }
    });
    return didRender;
  }

  function readCache(key) {
    try { return JSON.parse(sessionStorage.getItem(key)); } catch (e) { return null; }
  }
  function writeCache(key, value) {
    try { sessionStorage.setItem(key, JSON.stringify(value)); } catch (e) {}
  }

  // Shared by both data sources below: check cache, otherwise try each URL in
  // order with a bounded wait per attempt, otherwise fall back. Accepts a
  // list rather than one URL so a source with a documented mirror (the new
  // currency API explicitly recommends falling back from its jsDelivr CDN to
  // its Cloudflare Pages mirror) gets a real second attempt, not just a
  // single point of failure.
  function resolveCached(cacheKey, urls, timeoutMs, parse, fallback) {
    return new Promise(function (resolve) {
      var cached = readCache(cacheKey);
      if (cached !== null) return resolve(cached);

      var urlList = Array.isArray(urls) ? urls : [urls];

      function tryUrl(index) {
        if (index >= urlList.length) return resolve(fallback);

        var settled = false;
        var timer = setTimeout(function () {
          if (settled) return;
          settled = true;
          tryUrl(index + 1); // too slow — move to the next source rather than give up
        }, timeoutMs);

        fetch(urlList[index])
          .then(function (r) { return r.json(); })
          .then(function (data) {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            var value = parse(data);
            if (value === null) return tryUrl(index + 1); // responded, but unusable — next source
            writeCache(cacheKey, value);
            resolve(value);
          })
          .catch(function () {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            tryUrl(index + 1);
          });
      }
      tryUrl(0);
    });
  }

  var FETCH_TIMEOUT_MS = 5000;

  // 1. Visitor's currency, via geo IP lookup (cached per session so we only ask once).
  var currencyPromise = resolveCached(
    CURRENCY_CACHE_KEY,
    ['https://ipapi.co/json/'],
    FETCH_TIMEOUT_MS,
    function parseGeo(data) { return (data && data.currency) ? data.currency : null; },
    null
  );

  // 2. IDR exchange rates, live from currency-api (github.com/fawazahmed0/
  //    exchange-api — free, no key, no rate limit, 200+ currencies, updated
  //    daily), but ONLY looked up for currencies that don't have a manual
  //    override. Currencies with a manual number never depend on this call
  //    succeeding, or returning in time, at all.
  //
  //    Two URLs, tried in order: the maintainer's own docs explicitly warn to
  //    include a fallback, since the primary jsDelivr CDN has had outages
  //    before; the second URL is their documented Cloudflare Pages mirror of
  //    the same data.
  //
  //    IMPORTANT — unit direction: confirmed against a real fetch of this API
  //    (base=eur returned {"idr": 20558.11...}, i.e. 1 EUR = 20558 IDR) and
  //    against Frankfurter's own documented examples, which use the same
  //    convention: querying with base=IDR returns, for each currency, "how
  //    many units of that currency equal 1 IDR" — a tiny decimal (roughly
  //    0.00005 for USD), not "how many IDR per unit" (roughly 18,000). That
  //    is the opposite of what render() and the manual-override fields
  //    ("IDR per 1 USD", entered as e.g. 18063) expect. Inverting with
  //    1 / rawRate right here, once, at the parsing boundary, is what keeps
  //    that contract true everywhere downstream, rather than requiring every
  //    consumer to know which direction a given source uses.
  var ratesPromise = !autoCodes.length
    ? Promise.resolve({}) // everything on the list is manually overridden — nothing to fetch
    : resolveCached(
        RATES_CACHE_KEY,
        [
          'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/idr.json',
          'https://latest.currency-api.pages.dev/v1/currencies/idr.json'
        ],
        FETCH_TIMEOUT_MS,
        function parseRates(data) {
          var inner = data && data.idr; // nested object keyed by lowercase currency code
          if (!inner || typeof inner !== 'object') return null;
          var table = {};
          Object.keys(inner).forEach(function (lowerCode) {
            var rawRate = inner[lowerCode]; // "how many `code` equal 1 IDR" — a tiny decimal
            if (typeof rawRate !== 'number' || rawRate <= 0) return;
            table[lowerCode.toUpperCase()] = 1 / rawRate; // -> "IDR per 1 `code`", matching render()'s contract
          });
          return Object.keys(table).length ? table : null;
        },
        {}
      );

  Promise.all([currencyPromise, ratesPromise]).then(function (results) {
    var currencyCode = results[0];
    var liveTable = results[1] || {};

    // Manual overrides always win, and exist regardless of whether the live fetch succeeded.
    var finalTable = {};
    Object.keys(liveTable).forEach(function (c) { finalTable[c] = liveTable[c]; });
    manualCodes.forEach(function (c) { finalTable[c] = overrides[c]; });

    // shown reflects whether an estimate is actually visible right now, which
    // is a narrower condition than "a currency was detected": geo lookup can
    // resolve to a real currency (say RUB) that simply has no rate configured,
    // in which case nothing renders and nothing should be announced either.
    var shown = currencyCode ? render(currencyCode, finalTable) : false;

    window.SoulshineCurrencyEstimate = {
      currencyCode: shown ? currencyCode : null,
      // Re-applies the already-resolved currency/rates to whatever elements
      // exist right now, no re-fetch. Needed because switching a product
      // variant updates data-base-amount on the existing element rather than
      // creating a new one, and that element needs re-rendering for the new
      // price.
      refresh: function () {
        var didRender = currencyCode ? render(currencyCode, finalTable) : false;
        window.SoulshineCurrencyEstimate.currencyCode = didRender ? currencyCode : null;
      }
    };

    // A separate component (the "prices are estimated" notice) listens for
    // this rather than currency-estimate.js knowing anything about that UI.
    // Fires once per page load, whether or not an estimate ended up visible,
    // so a listener can also react correctly to the "nothing to show" case.
    document.dispatchEvent(new CustomEvent('soulshine:currency-estimate', {
      detail: { shown: shown, currencyCode: shown ? currencyCode : null }
    }));
  });
})();
